import { useEffect, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import {
  FormAccordion,
  FormAccordionItem,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Grid } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
  updateFeedInfoAfterAssetCreation,
  validateFundHoldingsAndDisplayError,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  FUND_HOLDING,
  FundHoldingFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/FundHoldingFormAccordionItemList';
import {
  INCOME_WITHDRAWAL,
  IncomeWithdrawalFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/IncomeWithdrawalFormAccordionItemList';

import { ContributionFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/ContributionFormAccordionItemList';
import {
  BENEFICIARY,
  BeneficiaryFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/BeneficiaryFormAccordionItemList';
import { FeeFormAccordionItemList } from 'src/financial/financial-data/financial-item/forms/common/FeeFormAccordionItemList';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import {
  FormTitleWithConnectedDataBadge,
  useConnectedDataIcon,
} from 'src/financial/financial-data/financial-item/forms/common/ConnectedDataBadge';
import {
  useValidateProviderReference,
  validateProviderReferenceAndSetError,
} from 'src/financial/financial-data/hooks/useValidateProviderReference';
import { useParams } from 'react-router-dom';
import {
  scrollToElementWithError,
  validateContributionEndDate,
} from 'src/financial/financial-data/financial-item/forms/common/validationUtils';

const INVESTMENT_AND_CASH_PRODUCT = 'InvestmentAndCash';
const CONTRIBUTION = 'InvestmentContribution';
const FEE_TYPE = 'InvestmentAndPensionFee';
export function InvestmentAndCash(props: ProductFormComponentProps) {
  const { onSave } = props;
  const [searchParams] = useSearchParams();
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { validateProviderReference } = useValidateProviderReference(clientId);

  const {
    isCreateMode,
    uiData,
    initialValues,
    onDeleteConfirm,
    feedInfo: initialFeedInfo,
    onConnectAsset,
  } = getFinancialFormSetupValues(INVESTMENT_AND_CASH_PRODUCT, props, [
    { product: FUND_HOLDING, field: 'fund_holdings' },
    { product: INCOME_WITHDRAWAL, field: 'income_withdrawals' },
    { product: CONTRIBUTION, field: 'contributions' },
    { product: BENEFICIARY, field: 'beneficiaries' },
    { product: FEE_TYPE, field: 'fees' },
  ]);

  const [feedInfo, setFeedInfo] = useState<FeedInfo>(initialFeedInfo);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues,
    validate: {
      [CONTRIBUTION]: {
        end_date: (value, values, path) => {
          return validateContributionEndDate(CONTRIBUTION, value, values, path);
        },
      },
    },
  });

  useEffect(() => {
    const providerReference = searchParams.get('providerReference');
    if (providerReference && isCreateMode) {
      form.setFieldValue(
        `${INVESTMENT_AND_CASH_PRODUCT}.provider_reference_number`,
        providerReference,
      );
    }
  }, [searchParams, isCreateMode, form]);

  const handleSave = async (values: FormValuesType) => {
    if (
      FUND_HOLDING in values &&
      Array.isArray(values[FUND_HOLDING]) &&
      INCOME_WITHDRAWAL in values &&
      Array.isArray(values[INCOME_WITHDRAWAL]) &&
      CONTRIBUTION in values &&
      Array.isArray(values[CONTRIBUTION]) &&
      BENEFICIARY in values &&
      Array.isArray(values[BENEFICIARY]) &&
      FEE_TYPE in values &&
      Array.isArray(values[FEE_TYPE])
    ) {
      const investmentData = values[INVESTMENT_AND_CASH_PRODUCT] as Record<string, unknown>;

      const isProviderReferenceValid = await validateProviderReferenceAndSetError(
        investmentData,
        clientId,
        form,
        INVESTMENT_AND_CASH_PRODUCT,
        validateProviderReference,
      );
      if (!isProviderReferenceValid) {
        return;
      }

      if (!validateFundHoldingsAndDisplayError(values, form)) {
        return;
      }

      const mappedValues = {
        ...investmentData,
        fund_holdings: values[FUND_HOLDING].map(item => ({ ...item })),
        income_withdrawals: values[INCOME_WITHDRAWAL].map(item => ({ ...item })),
        contributions: values[CONTRIBUTION].map(item => ({ ...item })),
        beneficiaries: values[BENEFICIARY].map(item => ({ ...item })),
        fees: values[FEE_TYPE].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[INVESTMENT_AND_CASH_PRODUCT]);

      updateFeedInfoAfterAssetCreation(
        form,
        INVESTMENT_AND_CASH_PRODUCT,
        isCreateMode,
        setFeedInfo,
      );
    }
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave, scrollToElementWithError)} autoComplete="off">
        <FormContent form={form} feedInfo={feedInfo} onConnect={onConnectAsset} />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({ form, feedInfo, onConnect }: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  const connectedDataIcon = useConnectedDataIcon(feedInfo.isUpdatedAutomatically);

  const summaryContent = (
    <Grid>
      <GridPair
        first={<FinancialFormField field="type" product={INVESTMENT_AND_CASH_PRODUCT} />}
        second={<FinancialFormField field="description" product={INVESTMENT_AND_CASH_PRODUCT} />}
      />
      <GridPair
        isHighlighted
        first={<FinancialFormField field="provider" product={INVESTMENT_AND_CASH_PRODUCT} />}
        second={
          <FinancialFormField
            field="provider_reference_number"
            product={INVESTMENT_AND_CASH_PRODUCT}
          />
        }
      />
      <GridPair
        first={<FinancialFormField field="start_date" product={INVESTMENT_AND_CASH_PRODUCT} />}
        second={<FinancialFormField field="status" product={INVESTMENT_AND_CASH_PRODUCT} />}
      />
      <GridPair
        first={<FinancialFormField field="is_joint" product={INVESTMENT_AND_CASH_PRODUCT} />}
      />
      <GridPair
        isHighlighted
        first={<FinancialFormField field="value" product={INVESTMENT_AND_CASH_PRODUCT} />}
        second={<FinancialFormField field="value_date" product={INVESTMENT_AND_CASH_PRODUCT} />}
      />
    </Grid>
  );

  const titleText = 'Assets > Cash and investment';
  const titleWithBadge = (
    <FormTitleWithConnectedDataBadge
      title={titleText}
      showBadge={feedInfo.isUpdatedAutomatically}
    />
  );

  return (
    <FinancialFormContainer title={titleWithBadge} feedInfo={feedInfo} onConnect={onConnect}>
      <FormAccordion
        defaultValue={[
          'summary',
          'fund_holdings',
          'income_withdrawal',
          'contribution',
          'beneficiaries',
          'fee',
        ]}
      >
        <FormAccordionItem value="summary" label="Summary" titleIcon={connectedDataIcon}>
          {feedInfo.isUpdatedAutomatically ? (
            <ReadOnlyWrapper>{summaryContent}</ReadOnlyWrapper>
          ) : (
            summaryContent
          )}
        </FormAccordionItem>
        <ContributionFormAccordionItemList
          form={form}
          isEditing={isEditing}
          contributionType={CONTRIBUTION}
        />
        <FeeFormAccordionItemList form={form} isEditing={isEditing} feeType={FEE_TYPE} />
        <FundHoldingFormAccordionItemList
          form={form}
          isEditing={isEditing}
          productType={INVESTMENT_AND_CASH_PRODUCT}
          titleIcon={connectedDataIcon}
          feedInfo={feedInfo}
        />
        <BeneficiaryFormAccordionItemList form={form} isEditing={isEditing} />
        <IncomeWithdrawalFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};
